/**
 * @fileoverview "You already have Romaine Lettuce in Bed 1" (E26, the small version; owner, 30 Oct), on the "Plant
 * something" sheet: the server's line, and under it its two choices, "Use that one" and "Add another", as a radio group
 * labelled by the line. The server picks the one chosen first ("use" at Planning, "add" when sown, growing or with no
 * stage); a tap changes it. The chosen one is filled with a check (theme colours, as the When chips); each is a thumb
 * tall and the two share the row.
 *
 * The app decides nothing: the line, the labels and the default are the server's (GET /todo/own/plant/plan's already).
 *
 * Where: PlantSomethingSheet.
 * API: none.
 * Words: none of the app's own (all the server's).
 *
 * @module own-todos/AlreadyChoice
 */

import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AlreadyKey, PlantAlready } from '@/shared/api/plantSomething';

/** A choice: a thumb tall, half the row each. */
const CHOICE = 'inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-full border px-3 text-[0.9375rem] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** AlreadyChoice's props. */
interface AlreadyChoiceProps {
  /** The server's answer: the line and the two choices. */
  already: PlantAlready;
  /** The choice picked. */
  value: AlreadyKey;
  /** A choice was tapped. */
  onChange: (next: AlreadyKey) => void;
  /** Saving: the choices wait. */
  disabled?: boolean;
}

/**
 * The line and the two choices.
 *
 * @param props - AlreadyChoiceProps.
 */
export const AlreadyChoice: React.FC<AlreadyChoiceProps> = ({ already, value, onChange, disabled = false }) => (
  <div>
    <p id="plant-something-already" className="mb-2 text-[0.9375rem] font-semibold leading-snug text-foreground">{already.line}</p>
    <div role="radiogroup" aria-labelledby="plant-something-already" className="flex gap-2">
      {already.choices.map((choice) => (
        <button
          key={choice.key}
          type="button"
          role="radio"
          aria-checked={value === choice.key}
          disabled={disabled}
          onClick={() => onChange(choice.key)}
          className={cn(CHOICE, value === choice.key ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-muted')}
        >
          {value === choice.key && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
          {choice.label}
        </button>
      ))}
    </div>
  </div>
);
